import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  TextField,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon, 
  Dashboard, 
  Agriculture as Farm, 
  Water as WaterIcon, 
  Policy as PolicyIcon, 
  Assignment, 
  Description, 
  Person, 
  Logout, 
  Calculate, 
  Warning as WarningIcon, 
  Info as InfoIcon, 
  AttachMoney as AttachMoneyIcon, 
  Help as HelpIcon,
  Analytics,
  RequestQuote as RequestIcon,
  Refresh,
  Settings,
  Search,
  PersonAdd,
  Event as EventIcon,
  VerifiedUser as KYCIcon,
  Notifications,
  Cloud,
  Shield as ShieldIcon,
  Storefront as StorefrontIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  Sell as SellIcon,
  ExpandLess,
  ExpandMore,
  Build as ToolsIcon,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { PERMISSIONS } from 'shared/utils/permissions';
import AlertBell from 'shared/components/AlertBell';
import WeatherWidget from 'shared/components/WeatherWidget';
import WelcomeOnboardingModal from 'shared/components/WelcomeOnboardingModal';

const drawerWidth = 240;

// ─── Farmer grouped sidebar builder ──────────────────────────
const useFarmerSidebarSections = (hasPermission) => {
  const P = PERMISSIONS;
  const sections = [];

  // ── COMMAND CENTER ────────────────────────────────
  sections.push({
    label: 'COMMAND CENTER',
    key: 'cmd',
    items: [
      { text: 'Dashboard', icon: <Dashboard />, path: '/farmer/dashboard' },
    ],
  });

  // ── OPERATIONS ────────────────────────────────────
  const opsItems = [];
  if (hasPermission(P.FARMER_FARMS_VIEW)) {
    opsItems.push({ text: 'Farms', icon: <Farm />, path: '/farmer/farms' });
  }
  if (hasPermission(P.FARMER_PONDS_VIEW)) {
    opsItems.push(
      { text: 'Ponds', icon: <WaterIcon />, path: '/farmer/ponds' },
      { text: 'Stocking', icon: <EventIcon />, path: '/farmer/stocking-details' },
    );
  }
  if (opsItems.length) {
    sections.push({ label: 'OPERATIONS', key: 'ops', items: opsItems });
  }

  // ── FINANCE ───────────────────────────────────────
  const finItems = [];
  if (hasPermission(P.FARMER_DASHBOARD)) {
    finItems.push({ text: 'Credit & Escrow', icon: <AccountBalanceIcon />, path: '/farmer/credit-escrow' });
    finItems.push({ text: 'Harvest & Settlement', icon: <SellIcon />, path: '/farmer/harvest-settlement' });
  }
  if (hasPermission(P.FARMER_POLICIES_VIEW) || hasPermission(P.FARMER_CLAIMS_VIEW) || hasPermission(P.FARMER_CLAIMS_SUBMIT)) {
    finItems.push({ text: 'Insurance & Claims', icon: <PolicyIcon />, path: '/farmer/insurance' });
  }
  if (finItems.length) {
    sections.push({ label: 'FINANCE', key: 'fin', items: finItems });
  }

  // ── TOOLS & SUPPORT (collapsible) ─────────────────
  const toolItems = [];
  if (hasPermission(P.FARMER_DASHBOARD)) {
    toolItems.push({ text: 'Documents', icon: <Description />, path: '/farmer/documents' });
    toolItems.push({ text: 'Premium Calculator', icon: <AttachMoneyIcon />, path: '/farmer/premium-calculator' });
  }
  if (hasPermission(P.FARMER_CLAIMS_VIEW)) {
    toolItems.push({ text: 'Claim Calculator', icon: <Calculate />, path: '/farmer/calculator' });
  }
  if (hasPermission(P.FARMER_DASHBOARD)) {
    toolItems.push({ text: 'Perils & Diseases', icon: <WarningIcon />, path: '/farmer/perils' });
  }
  if (hasPermission(P.FARMER_POLICIES_VIEW)) {
    toolItems.push({ text: 'Policy Terms', icon: <InfoIcon />, path: '/farmer/policy-terms' });
  }
  if (hasPermission(P.FARMER_DASHBOARD)) {
    toolItems.push({ text: 'Help Center', icon: <HelpIcon />, path: '/farmer/help' });
  }
  if (toolItems.length) {
    sections.push({ label: 'TOOLS & SUPPORT', key: 'tools', items: toolItems, collapsible: true });
  }

  return sections;
};

// ─── Flat nav item builder for non-farmer roles ─────────────
const useOtherRoleItems = (user, hasPermission, hasRole) => {
  const P = PERMISSIONS;
  const items = [];

  // Dashboard
  items.push({
    text: 'Dashboard',
    icon: <Dashboard />,
    path: `/${user?.role}/dashboard`,
  });

  // Agent / Operations
  if (hasPermission(P.OPERATIONS_DOCUMENTS)) {
    items.push({ text: 'Document Verification', icon: <Description />, path: '/agent/documents' });
  }
  if (hasPermission(P.OPERATIONS_LEADS_VIEW) || hasPermission(P.OPERATIONS_LEADS_MANAGE)) {
    items.push({ text: 'Request Management', icon: <RequestIcon />, path: '/agent/requests' });
  }

  // Admin / Internal
  if (hasPermission(P.SYSTEM_NOTIFICATIONS)) {
    items.push({ text: 'Alerts Management', icon: <Notifications />, path: '/admin/operations/alerts' });
  }
  if (hasPermission(P.SYSTEM_USERS_VIEW) || hasPermission(P.SYSTEM_USERS_MANAGE)) {
    items.push({ text: 'Farmer Management', icon: <Farm />, path: '/admin/operations/farmers' });
  }
  if (hasPermission(P.OPERATIONS_KYC_VIEW) || hasPermission(P.OPERATIONS_KYC_APPROVE)) {
    items.push({ text: 'KYC Verification', icon: <KYCIcon />, path: '/admin/operations/kyc' });
  }
  if (hasPermission(P.INSURANCE_VIEW) || hasPermission(P.INSURANCE_MANAGE)) {
    items.push({ text: 'Policy Approval', icon: <PolicyIcon />, path: '/admin/insurance/policies' });
  }
  if (hasPermission(P.INSURANCE_CLAIMS_VIEW) || hasPermission(P.INSURANCE_CLAIMS_PROCESS)) {
    items.push({ text: 'Claim Processing', icon: <Assignment />, path: '/admin/insurance/claims' });
  }
  if (hasPermission(P.INSURANCE_VIEW) || hasPermission(P.INSURANCE_MANAGE)) {
    items.push({ text: 'Risk Engine', icon: <ShieldIcon />, path: '/admin/insurance/analytics' });
  }
  if (hasPermission(P.CREDIT_VIEW) || hasPermission(P.CREDIT_MANAGE)) {
    items.push({ text: 'Credit Cockpit', icon: <AttachMoneyIcon />, path: '/admin/credit/applications' });
  }
  if (hasPermission(P.SYSTEM_ANALYTICS)) {
    items.push({ text: 'Analytics', icon: <Analytics />, path: '/admin/analytics' });
  }

  // Vendor
  if (hasPermission(P.MARKETPLACE_VIEW) && hasRole('vendor')) {
    items.push({ text: 'Orders', icon: <StorefrontIcon />, path: '/vendor/orders' });
  }
  if (hasPermission(P.MARKETPLACE_MANAGE) && hasRole('vendor')) {
    items.push({ text: 'Invoice Upload', icon: <ReceiptIcon />, path: '/vendor/invoices' });
  }
  if (hasPermission(P.MARKETPLACE_VIEW) && hasRole('vendor')) {
    items.push(
      { text: 'Payment History', icon: <AttachMoneyIcon />, path: '/vendor/payments' },
      { text: 'divider' },
      { text: 'Profile Settings', icon: <Settings />, path: '/vendor/profile' },
    );
  }

  // Settings
  if (hasPermission(P.SYSTEM_SETTINGS)) {
    items.push(
      { text: 'divider' },
      { text: 'Settings', icon: <Settings />, path: '/admin/settings' },
      { text: 'Weather Settings', icon: <Cloud />, path: '/admin/system/integrations/weather' },
    );
  }

  return items;
};

// ═════════════════════════════════════════════════════════════
// Layout Component
// ═════════════════════════════════════════════════════════════
const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openSections, setOpenSections] = useState({ tools: false });
  const { user, logout, hasRole, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isFarmer = hasRole('farmer');
  const farmerSections = useFarmerSidebarSections(hasPermission);
  const otherItems = useOtherRoleItems(user, hasPermission, hasRole);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { logout(); navigate('/login'); };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // ─── Shared styles for nav items ─────────────────────────
  const navItemSx = (active) => ({
    borderRadius: 2,
    '&.Mui-selected': {
      background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
      color: 'white',
      '&:hover': {
        background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
      },
      '& .MuiListItemIcon-root': { color: 'white' },
    },
    '&:hover': {
      background: 'rgba(33, 150, 243, 0.08)',
      borderRadius: 2,
    },
  });

  // ─── Section header style ────────────────────────────────
  const sectionHeaderSx = {
    px: 1.5,
    pt: 2,
    pb: 0.5,
    display: 'flex',
    alignItems: 'center',
  };

  // ─── Farmer grouped drawer ───────────────────────────────
  const farmerDrawerContent = (
    <List sx={{ px: 1, py: 1 }}>
      {farmerSections.map((section) => (
        <Box key={section.key}>
          {/* Section header */}
          {section.collapsible ? (
            <ListItemButton
              onClick={() => toggleSection(section.key)}
              sx={{ ...sectionHeaderSx, borderRadius: 1, py: 0.8, mt: 1 }}
            >
              <ToolsIcon sx={{ fontSize: 16, color: '#9e9e9e', mr: 1 }} />
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  color: '#9e9e9e',
                  lineHeight: 1,
                  flex: 1,
                }}
              >
                {section.label}
              </Typography>
              {openSections[section.key] ? (
                <ExpandLess sx={{ fontSize: 16, color: '#bdbdbd' }} />
              ) : (
                <ExpandMore sx={{ fontSize: 16, color: '#bdbdbd' }} />
              )}
            </ListItemButton>
          ) : (
            <Box sx={sectionHeaderSx}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  color: '#9e9e9e',
                  lineHeight: 1,
                }}
              >
                {section.label}
              </Typography>
            </Box>
          )}

          {/* Section items */}
          {section.collapsible ? (
            <Collapse in={openSections[section.key]} timeout="auto" unmountOnExit>
              <List disablePadding>
                {section.items.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.3 }}>
                      <ListItemButton
                        selected={active}
                        onClick={() => { navigate(item.path); setMobileOpen(false); }}
                        sx={{ ...navItemSx(active), pl: 3 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: active ? 'bold' : 'normal',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          ) : (
            section.items.map((item) => {
              const active = isActive(item.path);
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.3 }}>
                  <ListItemButton
                    selected={active}
                    onClick={() => { navigate(item.path); setMobileOpen(false); }}
                    sx={navItemSx(active)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: active ? 'bold' : 'normal',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })
          )}
        </Box>
      ))}
    </List>
  );

  // ─── Flat drawer for other roles ─────────────────────────
  const flatDrawerContent = (
    <List sx={{ px: 1, py: 2 }}>
      {otherItems.map((item, index) => {
        if (item.text === 'divider') {
          return <Divider key={`divider-${index}`} sx={{ my: 1 }} />;
        }
        const active = isActive(item.path);
        return (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={active}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              sx={navItemSx(active)}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontWeight: active ? 'bold' : 'normal' }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );

  // ─── Full drawer ─────────────────────────────────────────
  const drawer = (
    <Box sx={{ height: '100%', background: 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)' }}>
      <Toolbar sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)', color: 'white' }}>
        <Typography variant="h6" noWrap component="div" fontWeight="bold">
          🐟 Piscicare
        </Typography>
      </Toolbar>
      <Divider />
      {isFarmer ? farmerDrawerContent : flatDrawerContent}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" noWrap component="div">
              Welcome back, {user?.name}! 👨‍🌾
            </Typography>
          </Box>

          {/* Search and Add Farmer Button (only on Farmer Management page) */}
          {location.pathname === '/admin/users' && (
            <>
              <TextField
                size="small"
                placeholder="Search farmers..."
                sx={{
                  mr: 2,
                  width: 250,
                  display: { xs: 'none', md: 'block' },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'white',
                    },
                    '& input': {
                      color: 'white',
                      '&::placeholder': {
                        color: 'rgba(255,255,255,0.7)',
                        opacity: 1,
                      },
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <Box component="span" sx={{ color: 'rgba(255,255,255,0.7)', mr: 1, display: 'flex' }}>
                      <Search />
                    </Box>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                sx={{
                  mr: 2,
                  backgroundColor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.9)',
                  },
                  fontWeight: 600,
                  display: { xs: 'none', sm: 'flex' },
                }}
              >
                Add Farmer
              </Button>
            </>
          )}

          {/* Add New Farm Button (only on Farm Management page) */}
          {location.pathname === '/farmer/farms' && (
            <Button
              variant="contained"
              startIcon={<Farm />}
              onClick={() => navigate('/farmer/farms?action=add')}
              sx={{
                mr: 2,
                backgroundColor: 'white',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                },
                fontWeight: 600,
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              Add New Farm
            </Button>
          )}

          {/* Add New Pond Button (only on Pond Management page) */}
          {location.pathname === '/farmer/ponds' && (
            <Button
              variant="contained"
              startIcon={<WaterIcon />}
              onClick={() => navigate('/farmer/ponds?action=add')}
              sx={{
                mr: 2,
                backgroundColor: 'white',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                },
                fontWeight: 600,
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              Add New Pond
            </Button>
          )}

          {/* Refresh Button (only on dashboard) */}
          {location.pathname.includes('/dashboard') && (
            <IconButton
              color="inherit"
              onClick={() => window.location.reload()}
              sx={{ mr: 1 }}
              title="Refresh Dashboard"
            >
              <Refresh />
            </IconButton>
          )}

          {/* Weather Widget */}
          <WeatherWidget />

          {/* Alert Bell */}
          <AlertBell />

          {/* User Profile Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
              {user?.name}
            </Typography>
            <Button
              color="inherit"
              onClick={handleProfileMenuOpen}
              startIcon={<Avatar sx={{ width: 24, height: 24 }}>{user?.name?.charAt(0)}</Avatar>}
            >
              {user?.role}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
            >
              <MenuItem onClick={() => navigate('/profile')}>
                <ListItemIcon>
                  <Person fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>

      {/* Welcome modal — shows once after KYC verification */}
      <WelcomeOnboardingModal />
    </Box>
  );
};

export default Layout;
